import { Request, Response } from "express";
import mongoose from "mongoose";

import {
  Class,
  NOTIFICATION_TYPE,
  SCHEDULE_CHANGE_STATUS,
  ScheduleChangeRequest,
  Student,
  Teacher,
} from "../models";
import { notificationService } from "../services/notification-service";
import { logger } from "../utils/logger";

interface APIResponse<T = unknown> {
  success: boolean;
  message: string;
  data?: T;
}

interface AuthRequest extends Request {
  user?: { userId: string; role: string; schoolId?: string };
}

function formatRequest(reqDoc: InstanceType<typeof ScheduleChangeRequest>) {
  return {
    id: reqDoc.id,
    classId: reqDoc.classId?.toString(),
    teacherId: reqDoc.teacherId?.toString(),
    currentSchedule: reqDoc.currentSchedule,
    proposedSchedule: reqDoc.proposedSchedule,
    reason: reqDoc.reason,
    status: reqDoc.status,
    rejectionReason: reqDoc.rejectionReason ?? null,
    reviewedAt: reqDoc.reviewedAt ?? null,
    createdAt: reqDoc.createdAt,
  };
}

class ScheduleChangeController {
  async createScheduleChangeRequest(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { classId } = req.params;
      const { proposedSchedule, reason } = req.body;

      if (!userId) {
        res.status(401).json({ success: false, message: "Unauthorized" });
        return;
      }

      const trimmedReason = String(reason ?? "").trim();
      if (!Array.isArray(proposedSchedule) || !trimmedReason) {
        res.status(400).json({
          success: false,
          message: "proposedSchedule array and reason are required",
        });
        return;
      }

      const teacher = await Teacher.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });
      if (!teacher) {
        res.status(404).json({ success: false, message: "Teacher not found" });
        return;
      }

      const classItem = await Class.findOne({
        _id: classId,
        teacherIds: teacher._id,
        schoolId: teacher.schoolId,
      });
      if (!classItem) {
        res.status(404).json({ success: false, message: "Class not found" });
        return;
      }

      const request = await ScheduleChangeRequest.create({
        schoolId: teacher.schoolId,
        classId: classItem._id,
        teacherId: teacher._id,
        currentSchedule: classItem.schedule ?? [],
        proposedSchedule,
        reason: trimmedReason,
        status: SCHEDULE_CHANGE_STATUS.PENDING,
      });

      res.status(201).json({
        success: true,
        message: "Schedule change request submitted successfully",
        data: { request: formatRequest(request) },
      } as APIResponse);
    } catch (error) {
      logger.error("Create schedule change request error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to submit schedule change request",
      } as APIResponse);
    }
  }

  async getTeacherScheduleChangeRequests(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { status, page = 1, limit = 20 } = req.query;

      if (!userId) {
        res.status(401).json({ success: false, message: "Unauthorized" });
        return;
      }

      const teacher = await Teacher.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });
      if (!teacher) {
        res.status(404).json({ success: false, message: "Teacher not found" });
        return;
      }

      const requestFilter: Record<string, unknown> = { teacherId: teacher._id };
      if (status) requestFilter.status = status;

      const pageNum = Math.max(1, Number(page));
      const limitNum = Math.min(100, Math.max(1, Number(limit)));
      const skip = (pageNum - 1) * limitNum;

      const [requests, total] = await Promise.all([
        ScheduleChangeRequest.find({ ...requestFilter })
          .populate("classId", "className classCode")
          .sort({ createdAt: -1 })
          .skip(skip)
          .limit(limitNum),
        ScheduleChangeRequest.countDocuments({ ...requestFilter }),
      ]);

      res.status(200).json({
        success: true,
        message: "Schedule change requests retrieved successfully",
        data: {
          requests: requests.map(r => ({
            ...formatRequest(r),
            class: r.classId,
          })),
          pagination: {
            page: pageNum,
            limit: limitNum,
            total,
            pages: Math.ceil(total / limitNum),
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Get teacher schedule change requests error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve schedule change requests",
      } as APIResponse);
    }
  }

  async approveScheduleChangeRequest(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    await this.reviewRequest(req, res, true);
  }

  async rejectScheduleChangeRequest(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    await this.reviewRequest(req, res, false);
  }

  private async reviewRequest(
    req: AuthRequest,
    res: Response,
    approve: boolean,
  ): Promise<void> {
    try {
      const userId = req.user?.userId;
      const schoolId = req.user?.schoolId;
      const { id } = req.params;
      const { reason } = req.body;

      if (!userId || !schoolId) {
        res.status(401).json({ success: false, message: "Unauthorized" });
        return;
      }

      const request = await ScheduleChangeRequest.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
        status: SCHEDULE_CHANGE_STATUS.PENDING,
      });
      if (!request) {
        res.status(404).json({
          success: false,
          message: "Schedule change request not found",
        });
        return;
      }

      if (approve) {
        const classItem = await Class.findById(request.classId);
        if (!classItem) {
          res.status(404).json({ success: false, message: "Class not found" });
          return;
        }

        classItem.schedule = request.proposedSchedule;
        await classItem.save();

        request.status = SCHEDULE_CHANGE_STATUS.APPROVED;
        request.reviewedBy = new mongoose.Types.ObjectId(userId);
        request.reviewedAt = new Date();
        await request.save();

        const students = await Student.find({
          _id: { $in: classItem.studentIds ?? [] },
        }).select("userId");

        await notificationService.createBulkNotifications(
          students.map(s => s.userId.toString()),
          NOTIFICATION_TYPE.SCHEDULE_CHANGED,
          "Class Schedule Updated",
          `The schedule for ${classItem.className} has been updated`,
          {
            metadata: {
              classId: classItem.id,
              requestId: request.id,
            },
            schoolId,
          },
        );
      } else {
        if (!String(reason ?? "").trim()) {
          res.status(400).json({
            success: false,
            message: "reason is required when rejecting",
          });
          return;
        }
        request.status = SCHEDULE_CHANGE_STATUS.REJECTED;
        request.rejectionReason = String(reason).trim();
        request.reviewedBy = new mongoose.Types.ObjectId(userId);
        request.reviewedAt = new Date();
        await request.save();
      }

      res.status(200).json({
        success: true,
        message: approve
          ? "Schedule change request approved"
          : "Schedule change request rejected",
        data: { request: formatRequest(request) },
      } as APIResponse);
    } catch (error) {
      logger.error("Review schedule change request error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to review schedule change request",
      } as APIResponse);
    }
  }
}

export const scheduleChangeController = new ScheduleChangeController();
